import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import { useTranslation } from 'react-i18next';
import { getOrdersAsync } from './orderListSlice';
import FillWidthCell from '../../../app/components/FillWidthCell';
import StyledTableRow from '../../../app/components/StyledTableRow';
import { useNavigate, useParams } from 'react-router-dom';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatCurrency } from '../../../app/utils/formatters';
import EmptyScreen from '../../../app/components/EmptyScreen';

const OrderList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { orders, nextPageId, status } = useAppSelector(
    (state) => state.orders
  );
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  useEffect(() => {
    if (restaurantId) dispatch(getOrdersAsync({ restaurantId }));
  }, [dispatch, restaurantId]);

  const handleRowClick = (orderId: string) => {
    navigate(`/manager/restaurants/${restaurantId}/orders/${orderId}`);
  };

  const handleNextPage = () => {
    if (restaurantId && nextPageId) {
      setPageHistory((prev) => [...prev, nextPageId]);
      dispatch(getOrdersAsync({ restaurantId, nextPageId }));
    }
  };

  const handlePreviousPage = () => {
    if (restaurantId && pageHistory.length > 0) {
      const newHistory = [...pageHistory];
      newHistory.pop();
      const previousPageId = newHistory[newHistory.length - 1];
      setPageHistory(newHistory);
      dispatch(getOrdersAsync({ restaurantId, nextPageId: previousPageId }));
    }
  };

  if (status.includes('pending')) return <LoadingComponent />;

  if (!orders || orders.length === 0)
    return (
      <EmptyScreen
        title={t('orders.list.title')}
        emptyText={t('orders.list.empty')}
      />
    );

  return (
    <Box sx={{ maxWidth: '100%', margin: '0 auto', p: 0 }}>
      <ListTitleBox title={t('orders.list.title')} />

      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: 1,
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Table aria-label={t('orders.list.title')} size="small">
          <TableHead>
            <TableRow>
              <TableCell
                width="50px"
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                #
              </TableCell>
              <FillWidthCell
                align="left"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.order-number')}
              </FillWidthCell>
              <TableCell
                align="right"
                sx={{
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.total')}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  display: { xs: 'none', md: 'table-cell' },
                  py: 2,
                  fontWeight: 'bold',
                }}
              >
                {t('orders.list.header.status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order, index) => (
              <StyledTableRow
                key={order.id}
                onClick={() => handleRowClick(order.id)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                  cursor: 'pointer',
                }}
              >
                <TableCell
                  width="50px"
                  align="left"
                  component="th"
                  scope="row"
                  sx={{ py: 2 }}
                >
                  {index + 1}
                </TableCell>
                <TableCell align="left" sx={{ py: 2 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'medium',
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      #{order.tokenNumber}
                    </Typography>
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <OrderStatusLabel orderStatus={order.orderStatus} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 2 }}>
                  <Typography>{formatCurrency(order.total)}</Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', md: 'table-cell' },
                    verticalAlign: 'top',
                    py: 2,
                  }}
                >
                  <OrderStatusLabel orderStatus={order.orderStatus} />
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2 }}>
        <IconButton
          onClick={handlePreviousPage}
          disabled={pageHistory.length === 0}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          onClick={handleNextPage}
          disabled={!nextPageId}
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.action.disabledBackground,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default OrderList;
